import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, ArrowRight } from 'lucide-react';

const ExperienceCard = ({ exp, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
    className="relative pl-8 md:pl-0"
  >
    {/* Timeline Line (Desktop) */}
    <div className="hidden md:block absolute left-[50%] top-0 bottom-0 w-px bg-gradient-to-b from-white/50 via-white/20 to-transparent -translate-x-1/2" />

    <div className={`md:flex items-center justify-between gap-10 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>

      {/* Date - Desktop */}
      <div className="hidden md:block w-5/12 text-right">
        {index % 2 === 0 ? (
          <span className="text-2xl font-black tracking-tight gradient-text">
            {exp.duration}
          </span>
        ) : (
          <div className="text-right"> {/* Empty for alignment on other side */} </div>
        )}
        {index % 2 !== 0 && (
          <div className="p-8 bg-[#111928]/60 backdrop-blur-xl border border-white/10 rounded-3xl hover:bg-[#111928]/80 transition duration-500 text-left hover:border-white/30 group shadow-lg hover:shadow-white/10">
            <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-white transition-colors">{exp.title}</h3>
            <div className="flex items-center gap-2 text-white mb-6 font-bold text-sm tracking-widest uppercase">
              <Briefcase size={16} /> {exp.company}
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed text-sm font-medium">{exp.description}</p>
            <div className="flex flex-wrap gap-2">
              {exp.technologies.map(tech => (
                <span key={tech} className="px-4 py-1.5 text-xs font-semibold bg-white/10 text-white rounded-full border border-white/20 shadow-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Center Dot */}
      <div className="absolute left-0 md:left-1/2 w-5 h-5 rounded-full bg-[#0b0f19] border-4 border-white shadow-[0_0_15px_rgba(255,255,255,0.8)] md:-translate-x-1/2 mt-2 md:mt-0 z-10" />

      {/* Content - Desktop & Mobile */}
      <div className="w-full md:w-5/12 pl-6 md:pl-0">
        {/* Mobile Date */}
        <div className="md:hidden text-sm text-white mb-3 font-bold tracking-wider uppercase">
          {exp.duration}
        </div>

        {/* Card content for Mobile logic or Desktop Alternating */}
        <div className={`p-8 bg-[#111928]/60 backdrop-blur-xl border border-white/10 rounded-3xl hover:bg-[#111928]/80 transition duration-500 md:block hover:border-white/30 group shadow-lg hover:shadow-white/10 ${index % 2 !== 0 ? 'md:hidden' : ''}`}>
          <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-white transition-colors">{exp.title}</h3>
          <div className="flex items-center gap-2 text-white mb-6 font-bold text-sm tracking-widest uppercase">
            <Briefcase size={16} /> {exp.company}
          </div>

          <ul className="space-y-3 mb-6">
            {exp.responsibilities.map((resp, i) => (
              <li key={i} className="flex items-start text-gray-300 text-sm leading-relaxed font-medium">
                <ArrowRight size={16} className="mr-3 mt-0.5 text-white shrink-0" />
                {resp}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-white/10">
            {exp.technologies.map(tech => (
              <span key={tech} className="px-4 py-1.5 text-xs font-semibold bg-white/10 text-white rounded-full border border-white/20 shadow-sm">
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Duplicate content block for Desktop Left Side */}
        {index % 2 !== 0 && (
          <div className="hidden md:block md:text-left">
            <span className="text-2xl font-black tracking-tight gradient-text">
              {exp.duration}
            </span>
          </div>
        )}
      </div>

    </div>
  </motion.div>
);

const Experience = () => {
  const experiences = [
    {
      title: 'Software Development Intern',
      company: 'SUD Life Insurance',
      location: 'Mumbai, India',
      duration: 'Dec 2024 – Present',
      description: 'Architecting and securing an internal App Management System. Focused on optimizing database performance and delivering a seamless user experience.',
      responsibilities: [
        'Engineered RESTful APIs with Node.js and Express, integrating tightly with MSSQL databases.',
        'Implemented Role-Based Access Control (RBAC) to ensure secure and granular user authentication.',
        'Designed intuitive, accessible UI components in Figma for the "You Matter" enterprise application.'
      ],
      technologies: ['Node.js', 'Express', 'MSSQL', 'Figma', 'React']
    },
    {
      title: 'Technical Workshop Lead',
      company: 'QuestIT',
      location: 'Mumbai, India',
      duration: 'Aug 2024 - Apr 2025',
      description: 'Directed comprehensive technical workshops focusing on modern web development practices and core algorithms.',
      responsibilities: [
        'Mentored over 100 students in Web 2.0 architectures and Data Structures.',
        'Conducted live coding sessions to demonstrate end-to-end full-stack application deployment.',
        'Provided personalized code reviews and career guidance to aspiring developers.'
      ],
      technologies: ['React', 'Data Structures', 'Public Speaking']
    },
    {
      title: 'Cloud Computing Intern',
      company: 'Microsoft – Future Ready Talent',
      location: 'Remote',
      duration: 'May 2024 – Jun 2024',
      description: 'Cloud computing internship focused on leveraging Azure technologies to build and deploy scalable solutions.',
      responsibilities: [
        'Deployed and managed static web applications utilizing Azure cloud services.',
        'Integrated Azure Cognitive Services, such as Speech-to-Text, into responsive web applications.',
        'Streamlined development cycles by setting up continuous integration workflows with GitHub Actions.'
      ],
      technologies: ['Azure', 'GitHub Actions', 'Cloud Services']
    }
  ];

  return (
    <div className="min-h-screen bg-[#0b0f19] py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Glow Effects */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-white/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-black/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto relative z-10">
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-8">
              <Briefcase className="h-4 w-4 text-white" />
              <span className="text-white text-[10px] font-black tracking-widest uppercase">Career Path</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter">
              <span className="text-white">Professional </span>
              <span className="gradient-text">
                Experience
              </span>
            </h2>
            <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
              Milestones and pivotal roles that have shaped my technical foundation and professional journey.
            </p>
          </motion.div>
        </div>

        <div className="space-y-16 md:space-y-32">
          {experiences.map((exp, index) => (
            <ExperienceCard key={index} exp={exp} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Experience;
